/**
 * Wizard Analytics Hook
 *
 * Tracks signup wizard funnel metrics for conversion analysis.
 * All events are anonymized - NO PII is collected (no email, name, password).
 *
 * WP-AUTH-11: Multi-Step Signup Wizard
 */

import { useCallback, useRef, useEffect } from 'react';

/**
 * Analytics event payload (no PII)
 */
interface WizardAnalyticsEvent {
  eventName: string;
  timestamp: string;
  sessionId: string;
  properties: {
    wizardId: string;
    step?: number;
    stepName?: string;
    method?: 'email' | 'google' | 'microsoft' | 'apple';
    errorType?: string;
    errorField?: string;
    timeOnStepMs?: number;
    totalTimeMs?: number;
    attemptNumber?: number;
    document?: 'terms' | 'privacy';
    fromStep?: number;
    toStep?: number;
  };
}

// Generate anonymous session/wizard IDs (NOT user identifiers)
function generateAnonymousId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Hook for tracking wizard analytics
 *
 * @returns Analytics tracking functions
 */
export function useWizardAnalytics() {
  // Generate IDs on mount (persist for wizard session)
  const sessionIdRef = useRef<string>(generateAnonymousId());
  const wizardIdRef = useRef<string>(generateAnonymousId());
  const stepStartTimeRef = useRef<number>(Date.now());
  const wizardStartTimeRef = useRef<number>(Date.now());
  const captchaAttemptRef = useRef<number>(0);

  // Track when component mounts
  useEffect(() => {
    wizardStartTimeRef.current = Date.now();
    stepStartTimeRef.current = Date.now();
  }, []);

  /**
   * Send analytics event (to backend or client-side analytics)
   */
  const sendEvent = useCallback((event: WizardAnalyticsEvent) => {
    // In production, this would send to analytics endpoint
    // For now, we use console in dev mode for debugging
    if (import.meta.env.DEV) {
      // Log event name and properties (no PII)
      console.debug('[Analytics]', event.eventName, event.properties);
    }

    // MVP: Console logging in dev mode. Backend analytics endpoint is a post-MVP
    // feature. Events are structured for easy backend integration when ready.
  }, []);

  /**
   * Track wizard started (Step 1 viewed)
   */
  const trackWizardStarted = useCallback(() => {
    stepStartTimeRef.current = Date.now();
    wizardStartTimeRef.current = Date.now();

    sendEvent({
      eventName: 'signup_wizard_started',
      timestamp: new Date().toISOString(),
      sessionId: sessionIdRef.current,
      properties: {
        wizardId: wizardIdRef.current,
      },
    });
  }, [sendEvent]);

  /**
   * Track step viewed
   */
  const trackStepViewed = useCallback(
    (step: number, stepName: string) => {
      stepStartTimeRef.current = Date.now();

      sendEvent({
        eventName: 'signup_wizard_step_viewed',
        timestamp: new Date().toISOString(),
        sessionId: sessionIdRef.current,
        properties: {
          wizardId: wizardIdRef.current,
          step,
          stepName,
        },
      });
    },
    [sendEvent]
  );

  /**
   * Track step completed
   */
  const trackStepCompleted = useCallback(
    (step: number, stepName: string) => {
      const timeOnStepMs = Date.now() - stepStartTimeRef.current;

      sendEvent({
        eventName: 'signup_wizard_step_completed',
        timestamp: new Date().toISOString(),
        sessionId: sessionIdRef.current,
        properties: {
          wizardId: wizardIdRef.current,
          step,
          stepName,
          timeOnStepMs,
        },
      });

      // Reset timer for next step
      stepStartTimeRef.current = Date.now();
    },
    [sendEvent]
  );

  /**
   * Track back navigation
   */
  const trackStepBack = useCallback(
    (fromStep: number, toStep: number) => {
      sendEvent({
        eventName: 'signup_wizard_step_back',
        timestamp: new Date().toISOString(),
        sessionId: sessionIdRef.current,
        properties: {
          wizardId: wizardIdRef.current,
          fromStep,
          toStep,
        },
      });
    },
    [sendEvent]
  );

  /**
   * Track terms/privacy document reviewed
   */
  const trackDocumentReviewed = useCallback(
    (document: 'terms' | 'privacy') => {
      sendEvent({
        eventName: 'signup_wizard_terms_reviewed',
        timestamp: new Date().toISOString(),
        sessionId: sessionIdRef.current,
        properties: {
          wizardId: wizardIdRef.current,
          document,
        },
      });
    },
    [sendEvent]
  );

  /**
   * Track auth method selected
   */
  const trackMethodSelected = useCallback(
    (method: 'email' | 'google' | 'microsoft' | 'apple') => {
      sendEvent({
        eventName: 'signup_wizard_method_selected',
        timestamp: new Date().toISOString(),
        sessionId: sessionIdRef.current,
        properties: {
          wizardId: wizardIdRef.current,
          method,
        },
      });
    },
    [sendEvent]
  );

  /**
   * Track validation error
   */
  const trackValidationError = useCallback(
    (errorType: string, errorField: string) => {
      sendEvent({
        eventName: 'signup_wizard_validation_error',
        timestamp: new Date().toISOString(),
        sessionId: sessionIdRef.current,
        properties: {
          wizardId: wizardIdRef.current,
          errorType,
          errorField,
        },
      });
    },
    [sendEvent]
  );

  /**
   * Track CAPTCHA shown
   */
  const trackCaptchaShown = useCallback(() => {
    captchaAttemptRef.current = 0;

    sendEvent({
      eventName: 'signup_wizard_captcha_shown',
      timestamp: new Date().toISOString(),
      sessionId: sessionIdRef.current,
      properties: {
        wizardId: wizardIdRef.current,
      },
    });
  }, [sendEvent]);

  /**
   * Track CAPTCHA passed
   */
  const trackCaptchaPassed = useCallback(() => {
    const timeToCompleteMs = Date.now() - stepStartTimeRef.current;

    sendEvent({
      eventName: 'signup_wizard_captcha_passed',
      timestamp: new Date().toISOString(),
      sessionId: sessionIdRef.current,
      properties: {
        wizardId: wizardIdRef.current,
        timeOnStepMs: timeToCompleteMs,
      },
    });
  }, [sendEvent]);

  /**
   * Track CAPTCHA failed
   */
  const trackCaptchaFailed = useCallback(() => {
    captchaAttemptRef.current += 1;

    sendEvent({
      eventName: 'signup_wizard_captcha_failed',
      timestamp: new Date().toISOString(),
      sessionId: sessionIdRef.current,
      properties: {
        wizardId: wizardIdRef.current,
        attemptNumber: captchaAttemptRef.current,
      },
    });
  }, [sendEvent]);

  /**
   * Track form submitted
   */
  const trackSubmitted = useCallback(
    (method: 'email' | 'google' | 'microsoft' | 'apple') => {
      sendEvent({
        eventName: 'signup_wizard_submitted',
        timestamp: new Date().toISOString(),
        sessionId: sessionIdRef.current,
        properties: {
          wizardId: wizardIdRef.current,
          method,
        },
      });
    },
    [sendEvent]
  );

  /**
   * Track wizard completed (registration successful)
   */
  const trackCompleted = useCallback(
    (method: 'email' | 'google' | 'microsoft' | 'apple') => {
      const totalTimeMs = Date.now() - wizardStartTimeRef.current;

      sendEvent({
        eventName: 'signup_wizard_completed',
        timestamp: new Date().toISOString(),
        sessionId: sessionIdRef.current,
        properties: {
          wizardId: wizardIdRef.current,
          method,
          totalTimeMs,
        },
      });
    },
    [sendEvent]
  );

  /**
   * Track wizard abandoned (user leaves page)
   */
  const trackAbandoned = useCallback(
    (lastStep: number) => {
      const timeSpentMs = Date.now() - wizardStartTimeRef.current;

      sendEvent({
        eventName: 'signup_wizard_abandoned',
        timestamp: new Date().toISOString(),
        sessionId: sessionIdRef.current,
        properties: {
          wizardId: wizardIdRef.current,
          step: lastStep,
          totalTimeMs: timeSpentMs,
        },
      });
    },
    [sendEvent]
  );

  /**
   * Track error (server/network error)
   */
  const trackError = useCallback(
    (errorType: string) => {
      sendEvent({
        eventName: 'signup_wizard_error',
        timestamp: new Date().toISOString(),
        sessionId: sessionIdRef.current,
        properties: {
          wizardId: wizardIdRef.current,
          errorType,
        },
      });
    },
    [sendEvent]
  );

  return {
    trackWizardStarted,
    trackStepViewed,
    trackStepCompleted,
    trackStepBack,
    trackDocumentReviewed,
    trackMethodSelected,
    trackValidationError,
    trackCaptchaShown,
    trackCaptchaPassed,
    trackCaptchaFailed,
    trackSubmitted,
    trackCompleted,
    trackAbandoned,
    trackError,
  };
}

export default useWizardAnalytics;
